import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { useWebSocket } from '../hooks/useWebSocket';

const ChatPopupContext = createContext();

const API = '/api';  // Use relative URLs
const MAX_WINDOWS = 3;

export const useChatPopup = () => {
  const context = useContext(ChatPopupContext);
  if (!context) {
    throw new Error('useChatPopup must be used within ChatPopupProvider');
  }
  return context;
};

export const ChatPopupProvider = ({ children }) => {
  const authContext = useAuth();
  const user = authContext?.user; // Safe access to user
  
  const [windows, setWindows] = useState([]); // Array of open chat windows
  const [typingStatus, setTypingStatus] = useState({}); // {conversationId: {userId: timestamp}}
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  // WebSocket connection - only when user exists
  const { isConnected, sendMessage: wsSendMessage, addMessageHandler, enabled: wsEnabled } = useWebSocket(user?.id);

  // Load persisted state from localStorage on mount
  useEffect(() => {
    if (user && isDesktop) {
      const savedWindows = localStorage.getItem(`chat_windows_${user.id}`);
      if (savedWindows) {
        try {
          const parsed = JSON.parse(savedWindows);
          // Restore windows with their state and assign new IDs
          const restoredWindows = parsed.map(w => ({
            ...w,
            id: Date.now().toString() + Math.random(), // New unique ID
            loading: !w.minimized,
            messages: []
          }));
          setWindows(restoredWindows);
          
          // Load messages for each non-minimized window
          restoredWindows.forEach(w => {
            if (!w.minimized) {
              loadMessages(w.conversation.id, w.id);
            }
          });
        } catch (e) {
          console.error('Error loading saved windows:', e);
        }
      }
    }
  }, [user, isDesktop]);

  // Save windows state to localStorage
  useEffect(() => {
    if (user && windows.length > 0) {
      const toSave = windows.map(w => ({
        conversation: w.conversation,
        minimized: w.minimized,
        position: w.position,
        zIndex: w.zIndex,
        draft: w.draft
      }));
      localStorage.setItem(`chat_windows_${user.id}`, JSON.stringify(toSave));
    }
  }, [windows, user]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 992;
      setIsDesktop(desktop);
      
      // Close all windows on mobile
      if (!desktop && windows.length > 0) {
        setWindows([]);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [windows.length]);

  // WebSocket message handler
  useEffect(() => {
    if (!isConnected) return;

    const handleWebSocketMessage = (data) => {
      try {
        const { type } = data;

        if (type === 'new_message') {
          // New message received
          const { conversation_id, message } = data;
          
          if (!conversation_id || !message) {
            console.warn('Invalid new_message event:', data);
            return;
          }
          
          setWindows(prev => prev.map(w => {
            if (w.conversation && w.conversation.id === conversation_id) {
              return {
                ...w,
                messages: Array.isArray(w.messages) ? [...w.messages, message] : [message],
                unreadCount: w.minimized ? (w.unreadCount || 0) + 1 : 0
              };
            }
            return w;
          }));

          // Auto-mark as read if window is open and not minimized
          const window = windows.find(w => w.conversation && w.conversation.id === conversation_id);
          if (window && !window.minimized) {
            markAsRead(conversation_id);
          }
        } 
        else if (type === 'typing') {
          // Typing indicator
          const { conversation_id, user_id, is_typing } = data;
          
          if (!conversation_id || !user_id) return;
          
          setTypingStatus(prev => {
            const newStatus = { ...prev };
            if (!newStatus[conversation_id]) {
              newStatus[conversation_id] = {};
            }
            
            if (is_typing) {
              newStatus[conversation_id][user_id] = Date.now();
            } else {
              delete newStatus[conversation_id][user_id];
            }
            
            return newStatus;
          });

          // Clear typing after 3 seconds
          if (is_typing) {
            setTimeout(() => {
              setTypingStatus(prev => {
                const newStatus = { ...prev };
                if (newStatus[conversation_id] && newStatus[conversation_id][user_id]) {
                  delete newStatus[conversation_id][user_id];
                }
                return newStatus;
              });
            }, 3000);
          }
        }
        else if (type === 'messages_read') {
          // Messages marked as read
          const { conversation_id, message_ids } = data;
          
          if (!conversation_id || !Array.isArray(message_ids)) return;
          
          setWindows(prev => prev.map(w => {
            if (w.conversation && w.conversation.id === conversation_id && Array.isArray(w.messages)) {
              return {
                ...w,
                messages: w.messages.map(m => 
                  message_ids.includes(m.id) ? { ...m, read: true, read_at: new Date().toISOString() } : m
                )
              };
            }
            return w;
          }));
        }
        else if (type === 'message_delivered') {
          // Message delivered
          const { conversation_id, message_id } = data;
          
          if (!conversation_id || !message_id) return;
          
          setWindows(prev => prev.map(w => {
            if (w.conversation && w.conversation.id === conversation_id && Array.isArray(w.messages)) {
              return {
                ...w,
                messages: w.messages.map(m => 
                  m.id === message_id ? { ...m, status: 'delivered' } : m
                )
              };
            }
            return w;
          }));
        }
      } catch (error) {
        console.error('Error handling WebSocket message:', error, data);
      }
    };

    const unsubscribe = addMessageHandler(handleWebSocketMessage);
    return unsubscribe;
  }, [isConnected, windows, addMessageHandler]);

  const getNextWindowPosition = () => {
    const baseRight = 16; // 1rem = 16px
    const windowWidth = 360;
    const gap = 16;
    
    const openWindows = windows.filter(w => !w.minimized).length;
    
    return {
      right: baseRight + (openWindows * (windowWidth + gap)),
      bottom: baseRight
    };
  };

  const getMaxZIndex = () => {
    if (windows.length === 0) return 50;
    return Math.max(...windows.map(w => w.zIndex || 50)) + 1;
  };

  const openWindow = async (conversationData) => {
    if (!isDesktop) return;

    // Check if window already exists
    const existingIndex = windows.findIndex(w => w.conversation.id === conversationData.id);
    if (existingIndex !== -1) {
      // Bring to front and maximize
      setWindows(prev => prev.map((w, i) => 
        i === existingIndex 
          ? { ...w, minimized: false, zIndex: getMaxZIndex() }
          : w
      ));
      return;
    }

    // Check max windows
    if (windows.filter(w => !w.minimized).length >= MAX_WINDOWS) {
      alert(`You can only have ${MAX_WINDOWS} chat windows open at once. Please close one first.`);
      return;
    }

    const newWindow = {
      id: Date.now().toString(),
      conversation: conversationData,
      messages: [],
      minimized: false,
      loading: true,
      unreadCount: 0,
      position: getNextWindowPosition(),
      zIndex: getMaxZIndex(),
      draft: ''
    };

    setWindows(prev => [...prev, newWindow]);

    // Load messages
    try {
      await loadMessages(conversationData.id, newWindow.id);
      await markAsRead(conversationData.id);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const closeWindow = (windowId) => {
    setWindows(prev => prev.filter(w => w.id !== windowId));
  };

  const minimizeWindow = (windowId) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, minimized: true } : w
    ));
  };

  const maximizeWindow = (windowId) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId 
        ? { ...w, minimized: false, zIndex: getMaxZIndex(), unreadCount: 0 }
        : w
    ));

    // Mark as read
    const window = windows.find(w => w.id === windowId);
    if (window) {
      markAsRead(window.conversation.id);
    }
  };

  const bringToFront = (windowId) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, zIndex: getMaxZIndex() } : w
    ));
  };

  const updateWindowPosition = (windowId, position) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, position } : w
    ));
  };

  const updateDraft = (windowId, draft) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, draft } : w
    ));
  };

  const loadMessages = async (conversationId, windowId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWindows(prev => prev.map(w => 
        w.id === windowId 
          ? { ...w, messages: response.data, loading: false }
          : w
      ));
    } catch (error) {
      console.error('Error loading messages:', error);
      setWindows(prev => prev.map(w => 
        w.id === windowId ? { ...w, loading: false } : w
      ));
    }
  };

  // Manual refresh for when WebSockets are disabled
  const refreshMessages = async (windowId) => {
    const window = windows.find(w => w.id === windowId);
    if (!window) return;
    await loadMessages(window.conversation.id, windowId);
  };

  const sendMessageInWindow = async (windowId, content) => {
    const window = windows.find(w => w.id === windowId);
    if (!window || !content.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/messages/send`,
        {
          conversation_id: window.conversation.id,
          content: content.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Add message to window (WebSocket will also send it, but this is instant feedback)
      setWindows(prev => prev.map(w => 
        w.id === windowId 
          ? { ...w, messages: [...w.messages, response.data], draft: '' }
          : w
      ));

      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  const sendTypingIndicator = (conversationId, isTyping) => {
    if (isConnected) {
      wsSendMessage({
        type: 'typing',
        conversation_id: conversationId,
        is_typing: isTyping
      });
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/messages/mark-read`,
        { conversation_id: conversationId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const openConversationWithUser = async (userId, userName, userAvatar) => {
    if (!isDesktop) return null;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/messages/conversation/create`,
        { recipient_id: userId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const conversationData = {
        id: response.data.id,
        other_user: {
          id: userId,
          display_name: userName,
          avatar_url: userAvatar
        }
      };

      await openWindow(conversationData);
      return conversationData;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  const closeAllWindows = () => {
    setWindows([]);
    if (user) {
      localStorage.removeItem(`chat_windows_${user.id}`);
    }
  };

  const value = {
    windows,
    typingStatus,
    isDesktop,
    isConnected,
    wsEnabled,  // Whether WebSockets are enabled (for UI indicators)
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    bringToFront,
    updateWindowPosition,
    updateDraft,
    sendMessageInWindow,
    sendTypingIndicator,
    openConversationWithUser,
    closeAllWindows,
    refreshMessages  // Manual refresh for when WebSockets are disabled
  };

  return (
    <ChatPopupContext.Provider value={value}>
      {children}
    </ChatPopupContext.Provider>
  );
};
